'use client';

import React from 'react';
import { ColumnConfig, ColumnItem, ColumnLayout } from '@av/utils';
import RichContent from './rich-content';
import ImageBlock from './image';

// Calculate column widths based on the layout
const getColumnStyles = (layout: ColumnLayout, index: number): React.CSSProperties => {
  if (layout === '100') {
    return { width: '100%' };
  }

  if (layout === '50/50') {
    return { width: 'calc(50% - 0.5rem)' };
  }

  if (layout === '33/66') {
    return index === 0
      ? { width: 'calc(33.333% - 0.667rem)' }
      : { width: 'calc(66.666% - 0.333rem)' };
  }

  if (layout === '66/33') {
    return index === 0
      ? { width: 'calc(66.666% - 0.333rem)' }
      : { width: 'calc(33.333% - 0.667rem)' };
  }

  if (layout === '33/33/33') {
    return { width: 'calc(33.333% - 0.667rem)' };
  }

  if (layout === '25/50/25') {
    if (index === 0 || index === 2) {
      return { width: 'calc(25% - 0.75rem)' };
    } else {
      return { width: 'calc(50% - 0.5rem)' };
    }
  }

  return {};
};

const ColumnItemComponent = ({ item }: { item: ColumnItem }) => {
  if (item.type === 'image') {
    if (!item.value) {
      return null;
    }

    return (
      <ImageBlock
        image={item.value}
        alignedText={{ align: 'none', text: '' }}
        alt={item.value.alt || ''}
        width={item.value.width?.toString() || '100%'}
        height={item.value.height?.toString() || 'auto'}
        maxWidth="100%"
        radius="0"
        align={item.value.alignment || 'center'}
      />
    );
  } else if (item.type === 'richContent') {
    return <RichContent content={item.value} />;
  }

  return null;
};

interface ColumnsBlockProps {
  columns?: ColumnConfig;
}

const ColumnsBlock = ({ columns }: ColumnsBlockProps) => {
  if (!columns || !columns.columns || columns.columns.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4 md:flex-row md:gap-10 xl:gap-20">
        {columns.columns.map((column, index) => (
          <div
            key={column.id}
            className="flex flex-col max-md:!w-full"
            style={getColumnStyles(columns.layout, index)}
          >
            <ColumnItemComponent item={column} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ColumnsBlock;
